
import { useMemo, useState, useRef, useEffect } from 'react';
import { Button } from '@/components/shared/ui/button';
import { Badge } from '@/components/shared/ui/badge';
import { X, Plus, Minus, ShoppingCart as ShoppingCartIcon, RefreshCw, ChevronRight, Check, ChevronDown } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetClose } from '@/components/shared/ui/sheet';
import { useCartStore } from '@/features/user/stores/useCartStore';
import { useCartValidation } from '@/hooks/useCartValidation';
import { useCurrency } from '@/hooks/useCurrency';
import { useNavigate } from 'react-router-dom';
import { useFreeShippingThreshold, calculateShippingCost } from '@/hooks/useShippingCalculation';
import CheckoutButton from '@/components/uiverse/CheckoutButton';
import { getOptimizedImageUrl } from '@/lib/image-utils';
import 'animate.css';

interface ShoppingCartProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ShoppingCart = ({ isOpen, onOpenChange }: ShoppingCartProps) => {
  const navigate = useNavigate();

  const {
    state,
    removeItem,
    updateQuantity,
    getTotalItems
  } = useCartStore();

  // Stabilize options to prevent referential changes triggering the hook
  const cartValidationOptions = useMemo(() => ({
    enabled: isOpen,
    debounceMs: 500
  }), [isOpen]);

  const {
    validatedCart,
    isLoading: isLoadingCart,
  } = useCartValidation(cartValidationOptions);

  // No es necesario llamar revalidate manualmente aquí al abrir, 
  // useCartValidation ya lo hace cuando enabled={isOpen} cambia de false a true.

  const { formatPrice } = useCurrency();


  const itemCount = getTotalItems();
  // Memoize cart items with prices
  const cartItemsWithPrices = useMemo(() => {
    if (!validatedCart?.items) {
      return state.items.map(item => ({
        ...item,
        price: item.price || 0,
        maxStock: 99,
        available: true,
        image: item.image || '/placeholder.svg',
        name: item.name || 'Producto'
      }));
    }

    return state.items.map(localItem => {
      const validatedItem = validatedCart.items.find((v: any) => {
        if ((localItem as any).variantId || v.variantId) {
          return v.productId === localItem.productId && (v.variantId || '') === ((localItem as any).variantId || '');
        }
        // Fallback: match por atributos
        const sameProduct = v.productId === localItem.productId;
        const attrsA = localItem.attributes ? JSON.stringify(localItem.attributes) : '';
        const attrsB = v.attributes ? JSON.stringify(v.attributes) : '';
        return sameProduct && attrsA === attrsB;
      });

      return {
        ...localItem,
        name: validatedItem?.name ?? localItem.name ?? 'Producto',
        price: typeof validatedItem?.price === 'number' ? validatedItem.price : (typeof localItem.price === 'number' ? localItem.price : 0),
        image: validatedItem?.image ?? localItem.image ?? '/placeholder.svg',
        maxStock: typeof validatedItem?.stock === 'number' ? validatedItem.stock : 99,
        available: validatedItem?.available !== false
      };
    });
  }, [state.items, validatedCart]);

  // Memoize subtotal
  const subtotal = useMemo(() => {
    if (typeof validatedCart?.subtotal === 'number') return validatedCart.subtotal;
    return cartItemsWithPrices.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  }, [validatedCart, cartItemsWithPrices]);

  // Use authoritative shipping values from backend, fallback to local estimation while loading
  const loadingShipping = isLoadingCart;
  const localEstimatedShipping = calculateShippingCost(subtotal);
  const shippingCost = validatedCart?.shippingCost ?? (localEstimatedShipping > -1 ? localEstimatedShipping : 0);
  const isFree = validatedCart?.isFreeShipping ?? (shippingCost === 0 && localEstimatedShipping !== -1);


  const freeShippingThreshold = useFreeShippingThreshold();
  const shipping = shippingCost;
  const remainingForFreeShipping = Math.max(0, (freeShippingThreshold || 0) - subtotal);
  const progressPercentage = Math.min(100, (subtotal / (freeShippingThreshold || 1)) * 100);

  const discount = (subtotal * state.discountPercentage) / 100;
  const total = Math.max(0, subtotal + shipping - discount);

  // Scroll indicator logic
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      // Show indicator if there is more than 20px to scroll down
      const hasMoreContent = scrollHeight > clientHeight && (scrollTop + clientHeight < scrollHeight - 20);
      setShowScrollIndicator(hasMoreContent);
    }
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el && isOpen) {
      // Check immediately
      checkScroll();

      // Also check after a delay for items rendering
      const timer = setTimeout(checkScroll, 400);

      el.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);

      return () => {
        el.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
        clearTimeout(timer);
      };
    }
  }, [cartItemsWithPrices, isOpen]);

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[100%] sm:max-w-md p-0 font-montserrat flex flex-col gap-0 h-full border-none shadow-2xl bg-white">

        {/* 1. Header: Elegant and clean like filters/menu */}
        <SheetHeader className="px-6 py-3 border-b border-slate-100 flex-shrink-0 space-y-0">
          <div className="flex items-center justify-between w-full">
            <SheetTitle className="flex items-center gap-3 font-montserrat p-0 m-0">
              <ShoppingCartIcon size={20} className="text-slate-900" />
              <span className="font-montserrat font-bold text-slate-900 tracking-[1.5px] uppercase text-sm">CARRITO</span>
              <Badge variant="secondary" className="ml-1 bg-slate-100 text-slate-600 border-none font-bold">
                {itemCount}
              </Badge>
            </SheetTitle>
            <div className="flex items-center gap-2">
              {isLoadingCart && <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground mr-1" />}
              <SheetClose className="opacity-70 hover:opacity-100 transition-opacity p-1">
                <X className="h-5 w-5 text-slate-400" />
              </SheetClose>
            </div>
          </div>
        </SheetHeader>

        {/* 2. Free Shipping Progress Bar */}
        {state.items.length > 0 && (
          <div className="px-6 py-1 bg-slate-50/50 border-b border-slate-100">
            <div className="">
              {loadingShipping ? (
                <p className="text-[12px] text-slate-500 font-medium font-montserrat tracking-tight">Calculando envío…</p>
              ) : isFree ? (
                <div className="flex items-center gap-2">
                  <p className="text-[12px] font-bold text-green-600 font-montserrat animate__animated animate__pulse animate__infinite">
                    ¡ENVÍO GRATUITO!
                  </p>
                  <img src="/icons/IconsGiftConfetis.svg" alt="Regalo" className="w-5 h-5 mb-1" />
                </div>
              ) : !isFree && remainingForFreeShipping > 0 ? (
                <p className="text-[12px] text-slate-600 font-medium font-montserrat tracking-tight">
                  Te faltan <span className="font-bold text-primary">{formatPrice(remainingForFreeShipping, undefined, { showCode: true })}</span> para envío gratis
                </p>
              ) : null}
            </div>
            <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-700 ease-in-out ${isFree ? 'bg-green-500' : 'bg-primary'
                  }`}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        )}

        <div className="flex flex-col flex-1 min-h-0  justify-between relative overflow-hidden">
          {/* Items del carrito */}
          <div
            ref={scrollRef}
            className="overflow-y-auto overscroll-contain p-3 flex-1 min-h-0 scrollbar-thin-custom pb-12"
          >
            {state.items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-10 opacity-60">
                <div className="bg-slate-50 p-8 rounded-full mb-6">
                  <ShoppingCartIcon className="h-12 w-12 text-slate-300" />
                </div>
                <p className="text-slate-500 font-bold uppercase tracking-widest text-[12px]">Tu carrito está vacío</p>
                <Button variant="link" className="mt-4 text-primary font-bold text-sm uppercase" onClick={() => onOpenChange(false)}>
                  Ir a comprar <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className={`space-y-0 transition-opacity duration-200 ${isLoadingCart ? 'opacity-70' : 'opacity-100'}`}>
                {cartItemsWithPrices.map((item) => (
                  <div key={item.id} className="relative group p-0.5  last:mb-0">
                    {/* Product Item Layout - Compact Premium Redesign */}
                    <div className="flex gap-3 p-1.5 rounded-xl border border-slate-100 bg-white shadow-sm hover:shadow-md hover:border-slate-200 transition-all duration-300">
                      {/* 1. Image Holder */}
                      <div className="flex-shrink-0 relative overflow-hidden rounded-lg border border-slate-50 bg-slate-50/10">
                        <img
                          src={item.image ? getOptimizedImageUrl(item.image, { width: 100, quality: 80 }) : '/placeholder.svg'}
                          alt={item.name}
                          className="w-16 h-20 object-cover transition-transform duration-500 group-hover:scale-110"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/placeholder.svg';
                          }}
                        />
                      </div>

                      {/* 2. Details */}
                      <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                        <div className="space-y-1">
                          <div className="flex justify-between items-start gap-2">
                            <h3 className="font-jost text-black-900 text-semibold transition-colors tracking-[0.4px] uppercase text-[14px]">
                              {item.name}
                            </h3>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 rounded-full text-slate-300 hover:text-red-500 hover:bg-red-50 transition-all flex-shrink-0 -mt-1 -mr-1"
                              onClick={() => removeItem(item.id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>

                          {/* Attributes with enhanced styling */}
                          {item.attributes && Object.keys(item.attributes).length > 0 && (
                            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                              {Object.entries(item.attributes).map(([key, value]) => {
                                const isHexColor = /^#[0-9A-F]{6}$/i.test(value);
                                return (
                                  <div key={key} className="flex items-center gap-1.5 bg-slate-50 px-2 py-0.5 rounded-md border border-slate-100/50">
                                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">{key}</span>
                                    {isHexColor ? (
                                      <div className="w-2.5 h-2.5 rounded-full border border-white shadow-sm" style={{ backgroundColor: value }} />
                                    ) : (
                                      <span className="text-[10px] text-slate-600 font-bold uppercase">{value}</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Controls & Price Holder */}
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center bg-slate-50 border border-slate-100 rounded-full h-7 px-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 rounded-full text-slate-400 hover:text-slate-900 hover:bg-white shadow-sm transition-all"
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            >
                              <Minus className="h-2.5 w-2.5" />
                            </Button>
                            <span className="w-6 text-center text-[11px] font-bold text-slate-800 font-jost">
                              {item.quantity}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 rounded-full text-slate-400 hover:text-slate-900 hover:bg-white shadow-sm transition-all"
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              disabled={item.quantity >= item.maxStock}
                            >
                              <Plus className="h-2.5 w-2.5" />
                            </Button>
                          </div>
                          <div className="flex flex-col items-end">
                            <span className="font-jost font-semibold text-[14px] text-slate-800 tracking-tight">
                              {formatPrice(item.price * item.quantity, undefined, { showCode: true })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Visual Scroll Indicator (Fade out at bottom) */}
            {showScrollIndicator && (
              <div className="absolute bottom-[160px] left-0 right-0 h-10 pointer-events-none z-10 flex items-end justify-center animate-bounce duration-[2000ms]">
                <ChevronDown size={20} className="text-slate-500 drop-shadow-md" />
              </div>
            )}
          </div>

          {/* 3. Summary & Coupons */}
          {state.items.length > 0 && (
            <div className="border-t border-slate-100 p-4 bg-slate-50/50 space-y-2.5 flex-shrink-0">
              {/* Coupon - HIDDEN BY USER REQUEST ... */}

              {/* Price Table */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-[11px] font-bold text-slate-500 uppercase ">SUBTOTAL</span>
                  <span className="text-[14px] font-bold text-slate-900">{formatPrice(subtotal, undefined, { showCode: true })}</span>
                </div>

                {discount > 0 && (
                  <div className="flex justify-between items-center text-green-600">
                    <span className="font-jost font-semibold text-[14px] text-slate-800 tracking-tight">DESCUENTO</span>
                    <span className="text-[14px] font-bold">-{formatPrice(discount, undefined, { showCode: true })}</span>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <span className="text-[11px] font-bold text-slate-500 uppercase ">ENVÍO</span>
                  <div className="flex items-center gap-2">
                    {loadingShipping ? (
                      <span className="text-[12px] text-slate-400 italic">Calculando…</span>
                    ) : isFree ? (
                      <span className="flex items-center gap-1 text-green-600 font-bold text-[10px] bg-green-50 px-2 py-0.5 rounded-full border border-green-100/50">
                        <Check size={10} strokeWidth={3} />
                        Gratis
                      </span>
                    ) : (
                      <span className="text-[13px] font-bold text-slate-900">
                        {formatPrice(shipping, undefined, { showCode: true })}
                      </span>
                    )}
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-200 flex justify-between items-center">
                  <span className="font-jost font-semibold text-[14px] text-slate-800 tracking-tight">TOTAL</span>
                  <span className="text-[14px] font-bold text-primary">{formatPrice(total, undefined, { showCode: true })}</span>
                </div>
              </div>

              {/* Checkout Button */}
              <CheckoutButton
                onClick={() => {
                  onOpenChange(false);
                  navigate('/checkout');
                }}
              />
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
